import { useEffect, useState } from "react";
import { SlidersHorizontal, ShieldAlert, Activity, Bot } from "lucide-react";
import { PageHeader, Card, SectionTitle } from "@/components/common/PageHeader";
import { StatusChip } from "@/components/common/StatusChip";
import { Loading, Stat } from "@/pages/_shared";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const QUEUE_TONE = { healthy: "text-emerald-600", processing: "text-blue-600", idle: "text-slate-400" };
const CLASS_COLORS = {
  "PUBLIC": "#64748b", "INTERNAL": "#0ea5e9", "CONFIDENTIAL": "#8b5cf6",
  "SENSITIVE FINANCIAL": "#1a5fd0", "RESTRICTED IDENTITY": "#e11d48",
};

export default function MissionControl() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/mission-control/overview").then(({ data }) => setData(data)).catch(() => setError(true));
  }, [user]);

  if (user?.role !== "admin") {
    return (
      <div className="flex h-[60vh] flex-col items-center justify-center text-center" data-testid="mission-control-gate">
        <ShieldAlert size={40} className="text-amber-500" />
        <h2 className="mt-4 font-head text-xl font-extrabold text-slate-900">Admin access required</h2>
        <p className="mt-1 text-sm text-slate-500">Mission Control is restricted to MMT administrators (RBAC enforced).</p>
      </div>
    );
  }

  if (error) return <div className="py-10 text-center text-sm text-slate-400">Unable to load Mission Control.</div>;
  if (!data) return <Loading />;
  const s = data.system;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="mission-control-page">
      <PageHeader eyebrow="Mission Control · Admin" title="Mission Control"
        subtitle="System operations, agent queues and the platform audit trail."
        badge={<StatusChip state="SANDBOX" />} />

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <Stat label="Environment" value={s.environment} tone="blue" />
        <Stat label="Uptime" value={s.uptime} tone="green" />
        <Stat label="Active Agents" value={`${s.active_agents}/${s.total_agents}`} />
        <Stat label="Members" value={s.total_members} />
        <Stat label="AI Queries" value={s.ai_queries} tone="blue" />
        <Stat label="Agent Runs" value={s.agent_runs ?? 0} />
        <Stat label="Open Queues" value={s.open_queues} />
        <Stat label="Pending Approvals" value={s.pending_approvals ?? 0} />
      </div>

      <Card className="p-5">
        <SectionTitle icon={Bot}>Recent Agent Runs</SectionTitle>
        {(!data.runs || data.runs.length === 0) ? (
          <p className="text-[13px] text-slate-400">No agent tasks have been run yet. Assign one from the AI Workforce Control Room.</p>
        ) : (
          <div className="space-y-2">
            {data.runs.map((r, i) => (
              <div key={`agentrun-${i}`} className="flex items-start justify-between rounded-lg border border-slate-100 px-4 py-3" data-testid="agent-run-row">
                <div className="min-w-0">
                  <div className="text-[13px] font-medium text-slate-800">{r.agent} <span className="font-normal text-slate-500">· {r.role}</span>{r.auto && <span className="ml-2 rounded-full bg-[#b0862f]/10 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-[#b0862f]">Auto</span>}</div>
                  <div className="mt-0.5 truncate text-[12px] text-slate-500">{r.task}</div>
                  <div className="mt-0.5 font-mono text-[9px] uppercase tracking-wider text-[#1a5fd0]">{r.provider}:{r.model}</div>
                </div>
                <span className="ml-3 shrink-0 font-mono text-[10px] text-slate-400">{String(r.when).slice(0, 10)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card className="p-5">
          <SectionTitle icon={Activity}>Agent Queues</SectionTitle>
          <div className="space-y-2">
            {data.queues.map((q) => (
              <div key={q.name} className="flex items-center justify-between rounded-lg border border-slate-100 px-4 py-3" data-testid="queue-row">
                <div>
                  <div className="text-[13px] font-medium text-slate-800">{q.name}</div>
                  <div className="font-mono text-[10px] text-slate-400">owner · {q.owner}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-[13px] text-slate-600">{q.depth} in queue</span>
                  <span className={`font-mono text-[10px] uppercase ${QUEUE_TONE[q.state]}`}>● {q.state}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <SectionTitle icon={SlidersHorizontal}>Audit Trail</SectionTitle>
          <div className="space-y-2">
            {data.audit.map((a, i) => (
              <div key={`${a.actor}-${a.when}-${i}`} className="rounded-lg border border-slate-100 px-4 py-3" data-testid="audit-row">
                <div className="flex items-center justify-between">
                  <div className="text-[13px] font-medium text-slate-800">{a.actor}</div>
                  <span className="font-mono text-[10px] text-slate-400">{a.when}</span>
                </div>
                <div className="mt-0.5 text-[12px] text-slate-500">{a.action}</div>
                <span className="mt-1.5 inline-block font-mono text-[9px] font-semibold uppercase tracking-wider" style={{ color: CLASS_COLORS[a.class] }}>{a.class}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
